import { useEffect, useId, useRef, useState } from "react";
import { Box, ClickAwayListener, Popper, useMediaQuery } from "@mui/material";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { landingColors } from "../pages/Landing/landingColors";

const CLOSE_DELAY_MS = 120;

const HintTooltip = ({
  children,
  marker,
  content,
  ariaLabel,
  cardWidth = "min(320px, calc(100vw - 32px))",
}) => {
  const canHover = useMediaQuery("(hover: hover) and (pointer: fine)", {
    noSsr: true,
  });
  const prefersReducedMotion = useReducedMotion();
  const tooltipId = useId();
  const [anchorEl, setAnchorEl] = useState(null);
  const [arrowEl, setArrowEl] = useState(null);
  const [open, setOpen] = useState(false);
  // Popper stays mounted until the exit animation finishes.
  const [mounted, setMounted] = useState(false);
  const closeTimer = useRef(null);

  const cancelClose = () => clearTimeout(closeTimer.current);

  const show = () => {
    cancelClose();
    setMounted(true);
    setOpen(true);
  };

  const hide = () => {
    cancelClose();
    setOpen(false);
  };

  const scheduleHide = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      clearTimeout(closeTimer.current);
      setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const hoverHandlers = canHover
    ? { onMouseEnter: show, onMouseLeave: scheduleHide }
    : {};

  const handleClick = () => {
    if (canHover || !open) show();
    else hide();
  };

  // Only keyboard focus opens; a tap also focuses the button on some browsers.
  const handleFocus = (e) => {
    if (e.target.matches(":focus-visible")) show();
  };

  return (
    <ClickAwayListener onClickAway={() => open && hide()}>
      <Box component="span">
        <Box
          component="button"
          ref={setAnchorEl}
          type="button"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-describedby={open ? tooltipId : undefined}
          onClick={handleClick}
          onFocus={handleFocus}
          onBlur={hide}
          {...hoverHandlers}
          sx={{
            font: "inherit",
            color: "inherit",
            background: "none",
            border: 0,
            p: 0,
            m: 0,
            cursor: "pointer",
            display: "inline",
            borderRadius: 0.5,
            WebkitTapHighlightColor: "transparent",
            "&:focus-visible": {
              outline: `2px solid ${landingColors.primary}`,
              outlineOffset: 2,
            },
          }}
        >
          <Box
            component="span"
            sx={{
              textDecorationLine: "underline",
              textDecorationStyle: "dotted",
              textDecorationColor: landingColors.primary,
              textDecorationThickness: "2px",
              textUnderlineOffset: "4px",
              textDecorationSkipInk: "none",
            }}
          >
            {children}
          </Box>
          {marker && (
            <Box
              component="sup"
              sx={{
                color: landingColors.primary,
                fontWeight: 800,
                fontSize: "0.95em",
                lineHeight: 0,
                ml: 0.25,
              }}
            >
              {marker}
            </Box>
          )}
        </Box>

        <Popper
          id={tooltipId}
          open={mounted}
          anchorEl={anchorEl}
          placement="bottom"
          modifiers={[
            { name: "offset", options: { offset: [0, 10] } },
            { name: "preventOverflow", options: { padding: 16 } },
            { name: "flip", options: { padding: 16 } },
            {
              name: "arrow",
              enabled: Boolean(arrowEl),
              options: { element: arrowEl },
            },
          ]}
          sx={{
            zIndex: 1300,
            width: cardWidth,
            '&[data-popper-placement*="bottom"] .hint-tooltip-arrow': {
              top: -6,
            },
            '&[data-popper-placement*="top"] .hint-tooltip-arrow': {
              bottom: -6,
            },
          }}
        >
          {({ placement }) => {
            const isTop = placement.startsWith("top");
            return (
              <AnimatePresence onExitComplete={() => setMounted(false)}>
                {open && (
                  <motion.div
                    {...hoverHandlers}
                    initial={
                      prefersReducedMotion
                        ? { opacity: 0 }
                        : { opacity: 0, scale: 0.85, y: isTop ? 6 : -6 }
                    }
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={
                      prefersReducedMotion
                        ? { opacity: 0 }
                        : { opacity: 0, scale: 0.9, y: isTop ? 4 : -4 }
                    }
                    transition={{
                      duration: prefersReducedMotion ? 0.1 : 0.22,
                      ease: "easeOut",
                    }}
                    style={{
                      transformOrigin: isTop ? "bottom center" : "top center",
                      position: "relative",
                    }}
                  >
                    <Box
                      ref={setArrowEl}
                      className="hint-tooltip-arrow"
                      sx={{
                        position: "absolute",
                        width: 12,
                        height: 12,
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          inset: 0,
                          bgcolor: landingColors.surface,
                          transform: "rotate(45deg)",
                          borderRadius: "2px",
                        },
                      }}
                    />
                    <Box
                      sx={{
                        position: "relative",
                        bgcolor: landingColors.surface,
                        boxShadow: landingColors.cardShadow,
                        borderRadius: 3,
                        p: 2,
                        textAlign: "left",
                      }}
                    >
                      {content}
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>
            );
          }}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default HintTooltip;
